import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  Timestamp,
  updateDoc,
  serverTimestamp,
  deleteField,
  orderBy
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import type { DeliveryRecord, DeliverySearchFilters } from '../types/delivery';
import type { AssetRecord, AssetSearchFilters } from '../types/asset';
import type { Customer } from '../types/customer';
import type { UserCompanyRole } from '../utils/userCompaniesInterface'
// Auth functions with enhanced security and user-friendly error handling
export const getFriendlyErrorMessage = (error: any): string => {
  const errorCode = error?.code || error?.message || '';
  
  // Map Firebase error codes to user-friendly messages
  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid login credentials. Please check your email and password.';
    
    case 'auth/user-disabled':
      return 'Your account has been deactivated. Please contact admin support for assistance.';
    
    case 'auth/too-many-requests':
      return 'Too many login attempts. Please wait a few minutes before trying again.';
    
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';
    
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please use a different email or sign in instead.';
    
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password with at least 6 characters.';
    
    case 'ACCOUNT_DEACTIVATED_FAILED_ATTEMPTS':
      return 'Your account has been temporarily deactivated due to multiple failed login attempts. Please contact admin support.';
    
    default:
      // For unknown errors, provide a generic friendly message
      if (errorCode.includes('credential') || errorCode.includes('password') || errorCode.includes('email')) {
        return 'Invalid login credentials. Please check your email and password.';
      }
      return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
};

export const trackLoginAttempt = async (email: string, success: boolean) => {
  try {
    // Find user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return; // User not found, nothing to track
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    const currentAttempts = userData.failedLoginAttempts || 0;
    
    if (success) {
      // Reset failed attempts on successful login
      await updateDoc(userDoc.ref, {
        failedLoginAttempts: 0,
        lastFailedLoginAttempt: deleteField(),
        lastSuccessfulLogin: serverTimestamp()
      });
    } else {
      // Increment failed attempts
      const newAttempts = currentAttempts + 1;
      const updateData: any = {
        failedLoginAttempts: newAttempts,
        lastFailedLoginAttempt: serverTimestamp()
      };
      
      // Deactivate user after 3 failed attempts
      if (newAttempts >= 3) {
        updateData.active = false;
        updateData.deactivatedReason = 'Multiple failed login attempts';
        updateData.deactivatedAt = serverTimestamp();
        
        console.warn(`User ${email} deactivated after ${newAttempts} failed login attempts`);
      }
      
      await updateDoc(userDoc.ref, updateData);
      
      // Throw special error if account was just deactivated
      if (newAttempts >= 3) {
        const error = new Error('Account deactivated due to failed login attempts');
        (error as any).code = 'ACCOUNT_DEACTIVATED_FAILED_ATTEMPTS';
        throw error;
      }
    }
  } catch (error) {
    console.error('Error tracking login attempt:', error);
    // Don't throw here unless it's our special deactivation error
    if ((error as any).code === 'ACCOUNT_DEACTIVATED_FAILED_ATTEMPTS') {
      throw error;
    }
  }
};

export const signInUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Temporarily disable login tracking to avoid permission issues
    // TODO: Re-enable after fixing permissions
    /*
    // Track successful login (but don't fail if tracking fails)
    try {
      await trackLoginAttempt(email, true);
    } catch (trackError) {
      console.warn('Could not track successful login:', trackError);
    }
    */
    
    return userCredential;
  } catch (error) {
    console.error('Error signing in:', error);
    
    // Temporarily disable login tracking to avoid permission issues
    // TODO: Re-enable after fixing permissions
    /*
    // Track failed login attempt (only for credential-related errors)
    const errorCode = (error as any)?.code || '';
    if (errorCode.includes('credential') || errorCode.includes('password') || errorCode.includes('user-not-found')) {
      try {
        await trackLoginAttempt(email, false);
      } catch (trackError) {
        console.warn('Could not track failed login attempt:', trackError);
        // If tracking threw our special deactivation error, use that instead
        if ((trackError as any).code === 'ACCOUNT_DEACTIVATED_FAILED_ATTEMPTS') {
          throw trackError;
        }
      }
    }
    */
    
    // Throw user-friendly error
    const errorCode = (error as any)?.code || '';
    const friendlyMessage = getFriendlyErrorMessage(errorCode);
    const friendlyError = new Error(friendlyMessage);
    (friendlyError as any).code = errorCode;
    throw friendlyError;
  }
};

export const createUser = async (email: string, password: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    // Get user info before signing out
    const currentUser = auth.currentUser;
    const userId = currentUser?.uid;
    const userEmail = currentUser?.email;

    // Unsubscribe from push notifications before signing out
    try {
      const { unsubscribeFromPushNotifications } = await import('./pushNotifications');
      await unsubscribeFromPushNotifications(userId, userEmail || undefined);
    } catch (unsubError) {
      console.error('Error unsubscribing from push notifications:', unsubError);
      // Continue with logout even if unsubscribe fails
    }
    
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// User profile functions
export interface UserProfile {
  uid: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  phone?: string;
  company?: string;
  role?: 'user' | 'admin';
  globalRole?: string;
  active: boolean;
  isPasswordUser?: boolean;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export const createUserProfile = async (uid: string, userData: Partial<UserProfile>) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      const now = Timestamp.now();
      const profileData: UserProfile = {
        uid,
        email: userData.email || '',
        name: userData.name || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone,
        company: userData.company,
        role: userData.role || 'user',
        active: userData.active !== undefined ? userData.active : true,
        isPasswordUser: userData.isPasswordUser,
        created_at: now,
        updated_at: now
      };
      
      await setDoc(userRef, profileData);
      return profileData;
    }
    
    return userDoc.data() as UserProfile;
  } catch (error) {
    console.error('Error creating/getting user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = { uid: userDoc.id, ...userDoc.data() } as UserProfile;
      return userData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>) => {
  try {
    const userRef = doc(db, 'users', uid);
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    await setDoc(userRef, updateData, { merge: true });
    return updateData;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Report functions
export interface Report {
  id: string;
  userId?: string; // Optional for backward compatibility
  title?: string; // Optional for backward compatibility
  data?: unknown[]; // Optional for backward compatibility
  type?: string; // Optional for backward compatibility
  company: string;
  reportType: string;
  reportTypeName?: string;
  fileName: string;
  fileUrl: string;
  uploadedBy: string;
  uploadedAt: Timestamp;
  reportDate?: Timestamp;
  fileSize?: number;
  
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const getUserReports = async (selectedCompany: UserCompanyRole): Promise<Report[]> => {
  try {
    // // First get the user profile to get their company
    // const userProfile = await getUserProfile(userId);
    // console.log('📋 User profile for reports:', userProfile);
    
    // if (!userProfile?.company) {
    //   console.log('User has no company assigned, returning empty reports');
    //   return [];
    // }

    console.log('🔍 Searching reports for company:', selectedCompany);

    const reportsRef = collection(db, 'reports');
    const q = query(
      reportsRef, 
      where('company', '==', selectedCompany.companyName)
    );
    
    const querySnapshot = await getDocs(q);
    console.log('📊 Found reports:', querySnapshot.size);
    
    const reports: Report[] = [];
    
    querySnapshot.forEach((doc) => {
      const reportData = doc.data();
      console.log('📄 Report data:', { id: doc.id, company: reportData.company, fileName: reportData.fileName });
      reports.push({
        id: doc.id,
        ...reportData
      } as Report);
    });
    
    // Sort by reportDate if available, otherwise by createdAt in memory instead of in query
    reports.sort((a, b) => {
      const getDateForSort = (report: Report) => {
        if (report.reportDate) {
          return report.reportDate.seconds ? report.reportDate.seconds : (report.reportDate as unknown as Date).getTime() / 1000;
        }
        return report.createdAt.seconds ? report.createdAt.seconds : (report.createdAt as unknown as Date).getTime() / 1000;
      };
      
      const aDate = getDateForSort(a);
      const bDate = getDateForSort(b);
      return bDate - aDate; // Descending order (newest first)
    });
    
    console.log('📋 Final sorted reports:', reports.length);
    return reports;
  } catch (error) {
    console.error('Error getting user reports:', error);
    throw error;
  }
};

export const createReport = async (reportData: Omit<Report, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const reportsRef = collection(db, 'reports');
    const now = Timestamp.now();
    
    const newReport = {
      ...reportData,
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(reportsRef, newReport);
    return { id: docRef.id, ...newReport };
  } catch (error) {
    console.error('Error creating report:', error);
    throw error;
  }
};

// Support functions
export interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  subject: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const createSupportTicket = async (ticketData: Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt' | 'status'>) => {
  try {
    const ticketsRef = collection(db, 'support_tickets');
    const now = Timestamp.now();
    
    const newTicket = {
      ...ticketData,
      status: 'open' as const,
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(ticketsRef, newTicket);
    return { id: docRef.id, ...newTicket };
  } catch (error) {
    console.error('Error creating support ticket:', error);
    throw error;
  }
};

export const getUserSupportTickets = async (userId: string): Promise<SupportTicket[]> => {
  try {
    const ticketsRef = collection(db, 'support_tickets');
    const q = query(
      ticketsRef, 
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const tickets: SupportTicket[] = [];
    
    querySnapshot.forEach((doc) => {
      tickets.push({
        id: doc.id,
        ...doc.data()
      } as SupportTicket);
    });
    
    // Sort by createdAt in memory
    tickets.sort((a, b) => {
      const aDate = a.createdAt.seconds ? a.createdAt.seconds : (a.createdAt as unknown as Date).getTime() / 1000;
      const bDate = b.createdAt.seconds ? b.createdAt.seconds : (b.createdAt as unknown as Date).getTime() / 1000;
      return bDate - aDate; // Descending order (newest first)
    });
    
    return tickets;
  } catch (error) {
    console.error('Error getting user support tickets:', error);
    throw error;
  }
};

// Get available report types from Firestore
export interface ReportType {
  id: string;
  name: string;
  value: string;
  description: string;
  icon: string;
  color: string;
  active: boolean;
  order: number;
}

export const getReportTypes = async (): Promise<ReportType[]> => {
  try {
    const reportTypesRef = collection(db, 'report_types');
    const q = query(reportTypesRef, where('active', '==', true));
    const querySnapshot = await getDocs(q);
    const types: ReportType[] = [];
    
    querySnapshot.forEach((doc) => {
      types.push({
        id: doc.id,
        ...doc.data()
      } as ReportType);
    });
    
    // Sort by order field
    types.sort((a, b) => a.order - b.order);
    
    return types;
  } catch (error) {
    console.error('Error getting report types:', error);
    // Return default types as fallback
    return [
      { id: '1', name: 'Sales', value: 'sales', description: 'Sales reports', icon: '📊', color: 'green', active: true, order: 1 },
      { id: '2', name: 'Financial', value: 'financial', description: 'Financial reports', icon: '💰', color: 'blue', active: true, order: 2 },
      { id: '3', name: 'Feedback', value: 'feedback', description: 'Customer feedback', icon: '💬', color: 'purple', active: true, order: 3 }
    ];
  }
};

// Company management functions
export const getCompanyInfo = async (companyName: string) => {
  try {
    const companiesRef = collection(db, 'companies');
    const q = query(companiesRef, where('name', '==', companyName));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const companyDoc = querySnapshot.docs[0];
      const companyData = companyDoc.data();
      
      const result = {
        id: companyDoc.id,
        name: companyData.name,
        active: companyData.active !== false, // Default to true for backward compatibility
        logo: companyData.logo || null // Company logo URL
      };
      return result;
    }
    
    // Company not found
    return null;
  } catch (error) {
    console.error('Error getting company info:', error);
    throw error;
  }
};

export const checkUserAndCompanyActive = async (userProfile: UserProfile) => {
  if (!userProfile) {
    return { userActive: false, companyActive: false, canLogin: false, reason: 'User profile not found' };
  }

  // Check if user is active
  const userActive = userProfile.active !== false; // Default to true for backward compatibility
  
  // Check if user's company is active
  let companyActive = true;
  let companyCheckReason = null;
  
  if (userProfile.company) {
    try {
      const companyInfo = await getCompanyInfo(userProfile.company);
      
      if (companyInfo) {
        companyActive = companyInfo.active !== false; // Default to true for backward compatibility
      } else {
        companyActive = false;
        companyCheckReason = 'Company not found in database';
      }
    } catch (error) {
      console.error('Error checking company status:', error);
      companyActive = false;
      companyCheckReason = 'Error checking company status';
    }
  } else {
    // User has no company assigned - this should be allowed for new users
    companyActive = true; // Allow login if no company is assigned
  }

  const canLogin = userActive && companyActive;
  
  return {
    userActive,
    companyActive,
    canLogin,
    reason: !canLogin ? (
      !userActive ? 'User account is deactivated. Please contact your administrator.' :
      !companyActive ? (companyCheckReason || 'Company is deactivated. Please contact your administrator.') :
      'Unknown error'
    ) : null
  };
};

// Delivery Monitoring Functions

/**
 * Filters an array of DeliveryRecord to only unique combinations of tlo and wayBillNumber.
 * Keeps the record with the latest updated_at for each unique (tlo, wayBillNumber) pair.
 * @param deliveries Array of DeliveryRecord
 * @returns Array of unique DeliveryRecord
 */
// export function filterUniqueTloWayBill(deliveries: DeliveryRecord[]): DeliveryRecord[] {
//   const wayBillMap = new Map<string, DeliveryRecord>();
//   deliveries.forEach(delivery => {
//     const wayBillKey = (delivery.wayBillNumber || '').trim().toLowerCase();
    
//     if (!wayBillKey) return;
//     if (!wayBillMap.has(wayBillKey)) {
//       wayBillMap.set(wayBillKey, delivery);
//     } else {
//       const existing = wayBillMap.get(wayBillKey)!;
//       if (new Date(delivery.updated_at).getTime() > new Date(existing.updated_at).getTime()) {
//         wayBillMap.set(wayBillKey, delivery);
//       }
//     }
//   });
//   return Array.from(wayBillMap.values());
// }

// Customer functions
export const getCustomers = async (companyId: string): Promise<Customer[]> => {
  try {
    console.log('Fetching customers for company:', companyId);
    const customersRef = collection(db, 'customers');
    const q = query(customersRef, where('ownerCompanyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    
    const customers = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        customerName: data.customerCompanyName || '',
        ownerCompanyId: data.ownerCompanyId || '',
      } as Customer;
    });
    
    console.log(`Found ${customers.length} customers for company ${companyId}`);
    return customers;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

export const getDeliveryRecords = async (filters: DeliverySearchFilters | (DeliverySearchFilters & { company?: string })): Promise<DeliveryRecord[]> => {
  try {
    console.log('Searching delivery records with filters:', filters);
    const deliveryRef = collection(db, 'deliveryMonitoring');
    
    let querySnapshot;
    const queryConstraints = [];

    if ('company' in filters && filters.company) {
      queryConstraints.push(where('companyId', '==', filters.company));
    }

    // Add isCompleted filter if explicitly true or false
    if (typeof filters.isCompleted === 'boolean') {
      queryConstraints.push(where('isCompleted', '==', filters.isCompleted));
    }

    let dateField: 'dispatchDateTime' | 'updated_at' | 'created_at' = 'dispatchDateTime';
    if ((filters as any).dateField && ['dispatchDateTime', 'updated_at', 'created_at', 'completed_at'].includes((filters as any).dateField)) {
      dateField = (filters as any).dateField;
    }

    if (filters.fromDate && filters.toDate) {
      const fromDate = new Date(filters.fromDate);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = new Date(filters.toDate);
      toDate.setHours(23, 59, 59, 999);

      queryConstraints.push(where(dateField, '>=', Timestamp.fromDate(fromDate)));
      queryConstraints.push(where(dateField, '<=', Timestamp.fromDate(toDate)));
    }

    if (filters.customerId) {
      queryConstraints.push(where('customerId', '==', filters.customerId));
    }

    if (filters.isLatest === undefined) {
      filters.isLatest = true;
    }
    if (filters.isLatest) {
      queryConstraints.push(where('isLatest', '==', filters.isLatest));
    }

    queryConstraints.push(orderBy('updated_at', 'desc'));
    const q = query(deliveryRef, ...queryConstraints);
    querySnapshot = await getDocs(q);

    const voidStatuses = ['void', 'voided', 'deleted'];
    let deliveries = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      const statuses = [data.status, data.statusDetails]
        .map(status => String(status || '').trim().toLowerCase());

      return !data.isVoided && !data.voided_at
        && !statuses.some(status => voidStatuses.includes(status));
    }).map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        wayBillNumber: data.wayBillNumber || '',
        tlo: data.tlo || '',
        route: data.route || '',
        plateNumber: data.plateNumber || '',
        driverName: data.driverName || '',
        driverMobile: data.driverMobile || '',
        helperName: data.helperName || data.helper || '',
        helperMobile: data.helperMobile || '',
        statusDetails: data.statusDetails || '',
        dispatchDateTime: data.dispatchDateTime?.toDate ? data.dispatchDateTime.toDate() : new Date(data.dispatchDateTime),
        arrivalLoadingArea: data.arrivalLoadingArea?.toDate ? data.arrivalLoadingArea.toDate() : (data.arrivalLoadingArea ? new Date(data.arrivalLoadingArea) : undefined),
        arrivalUnloadingArea: data.arrivalUnloadingArea?.toDate ? data.arrivalUnloadingArea.toDate() : (data.arrivalUnloadingArea ? new Date(data.arrivalUnloadingArea) : undefined),
        demurrageCounting: data.demurrageCounting,
        billingStatus: data.billingStatus || '',
        podReturnedAt: data.podReturnedAt?.toDate
          ? data.podReturnedAt.toDate()
          : (data.podReturnedAt ? new Date(data.podReturnedAt) : undefined),
        company: data.company || '',
        customerId: data.customerId || '',
        created_at: data.created_at?.toDate ? data.created_at.toDate() : new Date(data.created_at),
        updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : new Date(data.updated_at),
        created_by: data.created_by || '',
        cargoWeight: data.cargoWeight || '',
        approvedDeadWeight: data.approvedDeadWeight || '',
        isCompleted: data.isCompleted || false,
        updated_by: data.updated_by || '',
        checklistId: data.checklistId || '',
        completed_at: data.completed_at?.toDate ? data.completed_at.toDate() : (data.completed_at ? new Date(data.completed_at) : undefined),
        customerAgingAsOf: data.customerAgingAsOf?.toDate ? data.customerAgingAsOf.toDate() : (data.customerAgingAsOf ? new Date(data.customerAgingAsOf) : undefined),
        internalAgingAsOf: data.internalAgingAsOf?.toDate ? data.internalAgingAsOf.toDate() : (data.internalAgingAsOf ? new Date(data.internalAgingAsOf) : undefined),
        isBilled: data.isBilled,
        estContractPrice: data.estContractPrice || 0,
        estDeliveryCost: data.estDeliveryCost || 0,
        isLatest: data.isLatest,
      } as unknown as DeliveryRecord;
    });

    // Apply client-side filters
    if (filters.wayBillNumber && filters.wayBillNumber.trim()) {
      const wayBillSearch = filters.wayBillNumber.toLowerCase().trim();
      deliveries = deliveries.filter(delivery =>
        delivery.wayBillNumber.toLowerCase().includes(wayBillSearch)
      );
    }

    if (filters.tlo && filters.tlo.trim()) {
      const tloSearch = filters.tlo.toLowerCase().trim();
      deliveries = deliveries.filter(delivery =>
        delivery.tlo.toLowerCase().includes(tloSearch)
      );
    }

    if (filters.plateNumber && filters.plateNumber.trim()) {
      const plateSearch = filters.plateNumber.toLowerCase().trim();
      deliveries = deliveries.filter(delivery => 
        delivery.plateNumber.toLowerCase().includes(plateSearch)
      );
    }

    if (filters.customerId && filters.customerId.trim()) {
      deliveries = deliveries.filter(delivery => 
        delivery.customerId === filters.customerId
      );
    }

    // console.log({deliveries})
    // deliveries = filterUniqueTloWayBill(deliveries);
    // console.log('📦 Unique WayBill records:',deliveries);
    
    // const uniqueDeliveries = Array.from(deliveries.values());

    // Sort by updated_at (newest first) for final display
    // uniqueDeliveries.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    // console.log(`Found ${deliveries.length} records after database filtering, filtered to ${uniqueDeliveries.length} unique records`);
    return deliveries;

  } catch (error) {
    console.error('Error fetching delivery records:', error);
    throw error;
  }
};

// Asset Monitoring Functions
export const getAssetRecords = async (filters: AssetSearchFilters): Promise<AssetRecord[]> => {
  try {
    console.log('Searching asset records with filters:', filters);
    const assetRef = collection(db, 'assetMonitoring');
    
    let querySnapshot;
    
    // Build query with company filter
    const queryConstraints = [];
    
    // Company filter - required for cost optimization
    if (filters.company) {
      queryConstraints.push(where('company', '==', filters.company));
    }
    
    // Category filter at database level
    if (filters.category && filters.category.trim()) {
      queryConstraints.push(where('category', '==', filters.category));
    }

    // Status filter at database level  
    if (filters.status && filters.status.trim()) {
      queryConstraints.push(where('status', '==', filters.status));
    }

    // Order by updated_at
    queryConstraints.push(orderBy('updated_at', 'desc'));

    console.log('Asset query constraints:', queryConstraints);
    const q = query(assetRef, ...queryConstraints);
    querySnapshot = await getDocs(q);

    let assets = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        assetCode: data.assetCode || '',
        assetName: data.assetName || '',
        category: data.category || '',
        status: data.status || '',
        location: data.location || '',
        assignedTo: data.assignedTo || '',
        serialNumber: data.serialNumber || '',
        value: data.value || 0,
        description: data.description || '',
        purchaseDate: data.purchaseDate?.toDate ? data.purchaseDate.toDate() : (data.purchaseDate ? new Date(data.purchaseDate) : undefined),
        warrantyExpiry: data.warrantyExpiry?.toDate ? data.warrantyExpiry.toDate() : (data.warrantyExpiry ? new Date(data.warrantyExpiry) : undefined),
        maintenanceSchedule: data.maintenanceSchedule?.toDate ? data.maintenanceSchedule.toDate() : (data.maintenanceSchedule ? new Date(data.maintenanceSchedule) : undefined),
        lastMaintenanceDate: data.lastMaintenanceDate?.toDate ? data.lastMaintenanceDate.toDate() : (data.lastMaintenanceDate ? new Date(data.lastMaintenanceDate) : undefined),
        notes: data.notes || '',
        company: data.company || '',
        image: data.image || '',
        created_at: data.created_at?.toDate ? data.created_at.toDate() : new Date(data.created_at),
        updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : new Date(data.updated_at),
        created_by: data.created_by || '',
        updated_by: data.updated_by || ''
      } as AssetRecord;
    });

    // Apply client-side filters for asset name search
    if (filters.assetName && filters.assetName.trim()) {
      console.log('assetName filter applied:', filters.assetName);
      const assetNameSearch = filters.assetName.toLowerCase().trim();
      assets = assets.filter(asset =>
        asset.assetName.toLowerCase().includes(assetNameSearch)
      );
    }

    if (filters.assignedTo && filters.assignedTo.trim()) {
      console.log('assignedTo filter applied:', filters.assignedTo);
      const assignedToSearch = filters.assignedTo.toLowerCase().trim();
      assets = assets.filter(asset =>
        (asset.assignedTo ?? '').toLowerCase().includes(assignedToSearch)
      );
    }

    console.log(`Found ${assets.length} asset records`);
    return assets;

  } catch (error) {
    console.error('Error fetching asset records:', error);
    throw error;
  }
};
